import { Directive, Input, TemplateRef, OnInit, OnDestroy, ElementRef, HostListener, ViewContainerRef } from '@angular/core';
import { OverlayRef, Overlay, OverlayPositionBuilder, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentType, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';

export let defPos: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetX: 0,
    offsetY: -15,
    panelClass: 'top'
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 15,
    offsetY: 0,
    panelClass: 'right'
  },
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetX: 0,
    offsetY: 15,
    panelClass: 'bottom'
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -15,
    offsetY: 0,
    panelClass: 'left'
  }
];

export const posOptions: { [key: string]: ConnectedPosition[] } = {
  top: [defPos[0], // Primary position
    ...defPos.filter((_, i) => i !== 0) // Fallback positions
  ],
  right: [defPos[1],
    ...defPos.filter((_, i) => i !== 1)
  ],
  bottom: [defPos[2],
    ...defPos.filter((_, i) => i !== 2)
  ],
  left: [defPos[3],
    ...defPos.filter((_, i) => i !== 3)
  ]
};

@Directive({
  standalone: true,
  selector: '[customTooltip]',
})
export class CustomTooltipDirective implements OnInit, OnDestroy {
  @Input('customTooltip') tooltipContent: TemplateRef<any> | ComponentType<any> | any = null;
  @Input('customTooltipType') customTooltipType: 'default' = 'default';
  @Input('prefPos') prefPos: 'top' | 'right' | 'bottom' | 'left' = 'top';

  private _overlayRef!: OverlayRef;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
  ) { }

  ngOnInit(): void {
    if (this.tooltipContent) {
      const position = this.overlayPositionBuilder
        .flexibleConnectedTo(this.elementRef)
        .withPositions(posOptions[this.prefPos])
        .withPush(true); // Enable pushing to viewport

      this._overlayRef = this.overlay.create({
        positionStrategy: position,
        scrollStrategy: this.overlay.scrollStrategies.close(),
        panelClass: ['custom-tooltip', this.customTooltipType],
        hasBackdrop: false,
      });

      // Clean up position changes subscription
      position.positionChanges.subscribe();
    }
  }

  ngOnDestroy(): void {
    // Properly clean up the overlay when directive is destroyed
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  @HostListener('mouseenter')
  private _show(): void {
    if (this._overlayRef && !this._overlayRef.hasAttached()) {
      let containerPortal: TemplatePortal<any> | ComponentPortal<any>;

      if (this.tooltipContent instanceof TemplateRef) {
        containerPortal = new TemplatePortal(
          this.tooltipContent, 
          this.viewContainerRef
        );
      } else {
        containerPortal = new ComponentPortal(
          this.tooltipContent, 
          this.viewContainerRef
        );
      }

      this._overlayRef.attach(containerPortal);
    }
  }

  @HostListener('mouseout', ['$event'])
  private _hide(e: MouseEvent): void {
    if (this._overlayRef && this._overlayRef.hasAttached()) {
      // Check if mouse is leaving the host element and not entering the tooltip
      const relatedTarget = e.relatedTarget as HTMLElement;
      const isLeavingToTooltip = this._overlayRef.overlayElement.contains(relatedTarget);
      
      if (!isLeavingToTooltip) {
        this._overlayRef.detach();
      }
    }
  }
}